import common from '../common';
import Player from '../physics/Player';
import ScrollButton from '../ui/scrollbutton';
import VariableSelector from '../ui/VariableSelector';
import utils from '../utils';
import Command from './Command';
import JumpCommand from './JumpCommand';
import MorphCommand from './MorphCommand';
import RepeatCommand from './RepeatCommand';
import TurnCommand from './TurnCommand';
import WalkCommand from './WalkCommand';

/*
	Q: Holy smokes! Why is the file so huge?
	A: Feature creeping and deadlines! Lots of features were added that weren't originally planned
	There's lots of things to handle. This file mainly handles the Code Block
		drag & drop,
		repeat command handling,
		reordering,
		sizing,
		variables,
		running the code,
		deleting code,
		lots of tweens,
		some tutorial logic

	Running code & creating code could have been put into seperate files, probably.
*/
export default class CommandInterface {
	constructor(player, gameScene, tutorialMode) {
		this.executableCommandsPre = []; // Array of commands prior to execution. Used to revert back to afterwards
		this.executableCommands = []; // Array of commands that are to be executed. Gets expanded with new commands depending on repeat value
		this.toolboxItems = [];

		/**
		 * @type {Command[]}
		 */
		this.commands = []; // Array of all commands to determine resizing, etc.

		this.commandContainer = null;
		this.templateCommand = null;
		this.currentMoveIndex = 0;
		this.selectorPoint = new PIXI.Point();
		this.gameScene = gameScene;
		this.player = player;
		this.btnScrollDown = null;
		this.needSelector = null;
		this.scrollOffset = null;
		this.btnScrollUp = null;
		this.commandMask = null;
		this.scrollView = null;
		this.scrollbar = null;
		this.selector = null;
		this.ui = null;
		this.emptyCollide = false;
		this.isScrolling = false;
		this.isTweening = false;
		this.running = false;
		this.isDragging = false;
		this.canStart = true;
		this.scrollStates = {up: false, down: false};
		this.canRun = true;
		this.currentCommand = -1;
		this.commandCount = 0;
		this.stepCount = 0;
		this._maxVisibleCommands = -1;
		this._maxContainerHeight = -1;
		this._destroyed = false;

		this.signals = {
			levelcomplete  : new P3.utils.MiniSignal(),
			start          : new P3.utils.MiniSignal(),
			end            : new P3.utils.MiniSignal(),
			blockchange    : new P3.utils.MiniSignal(),
			updateTutorial : new P3.utils.MiniSignal(),
			setvisibility  : new P3.utils.MiniSignal()
		};

		this._tutorial2HasDeleted = false;
		this._tutorialGroup = 0;
		this.tutorialStage = 0;
		this.tutorialMode = tutorialMode;
		this.tweens = new P3.utils.TWEEN.Group();
		this.tutorialTweens = new P3.utils.TWEEN.Group();
		this._fingerScrollBounds = {
			upper : new PIXI.Rectangle(),
			lower : new PIXI.Rectangle()
		};

		this.toolboxSpacing = 20;
		this.toolboxWidth = common.commandDimensions.width * 2 + (2 * this.toolboxSpacing);

		this.init();
	}

	init() {
		common.commandInterface = this;

		this._maxVisibleCommands = Math.floor((P3.View.height - 110) / common.commandDimensions.height);
		this._maxContainerHeight = this._maxVisibleCommands * common.commandDimensions.height;

		this.ui = this.createUI();

		this.ui.toolbox = this.createToolbox();
		this.ui.toolbox.x = (common.STAGE_WIDTH * 0.5) - (this.toolboxWidth * 0.5);
		this.ui.toolbox.y = -this.ui.toolbox.height;
		this.ui.toolbox.visible = false;

		this.ui.addChild(this.ui.toolbox);
		this.ui.addChild(this.ui.add);

		const toolboxMask = new PIXI.Graphics();
		toolboxMask.beginFill(0xFF0000, 0.2);
		toolboxMask.drawRect(0, 0, this.ui.toolbox.width, this.ui.toolbox.height);
		toolboxMask.endFill();
		toolboxMask.x = (common.STAGE_WIDTH * 0.5) - (toolboxMask.width * 0.5);
		toolboxMask.y = 82;
		this.ui.addChild(toolboxMask);
		this.ui.toolbox.mask = toolboxMask;

		this.player.signals.died.add(this.onFail, this);
		this.player.signals.success.once(this.onSuccess, this);

		this.ui.main = new PIXI.Container();
		this.ui.addChild(this.ui.main);

		// command panel mask
		this.commandMask = new PIXI.Graphics();
		this.commandMask.beginFill(0xFF0000, 0.2);
		this.commandMask.drawRect(0, 0, 670, this._maxContainerHeight);
		this.commandMask.endFill();

		this.commandContainer = new PIXI.Container();
		this.commandContainer.mask = this.commandMask;
		this.ui.main.addChild(this.commandContainer, this.commandMask);

		this.scrollView = new P3.utils.ScrollView();
		this.scrollView.interactive = false;
		this.scrollView.contentBounds = new PIXI.Rectangle(Number.NEGATIVE_INFINITY, 0, Number.POSITIVE_INFINITY, 0);
		this.scrollView.hitArea = new PIXI.Rectangle(200, 372);
		this.scrollView._content = this.commandContainer;
		this.ui.addChild(this.scrollView);

		this.createCommands();

		this.ui.interactive = true;
		this.ui.on('pointerupoutside', this.onPointerDown, this);
		this.ui.on('pointerup', this.onPointerDown, this);

		const local = this.commands[0].toGlobal(this.ui);
		this.templateCommand = new PIXI.Container();

		let nineslice = new PIXI.mesh.NineSlicePlane(P3.assets.texture('ui_function_9slice_playback'), 40, 18, 10, 18);
		nineslice.width = common.commandDimensions.width + 15;		// I dont know what 15 is but it works
		nineslice.height = common.commandDimensions.height + 15;	// 	    "		"		"		"
		nineslice.alpha = 0.5;
		nineslice.position.set(nineslice.width * -0.5, nineslice.height * -0.5);
		this.templateCommand.addChild(nineslice);

		this.templateCommand.position.set(local.x + 43, 100);
		this.templateCommand.visible = false;
		this.templateCommand.nineslice = nineslice;
		this.ui.main.addChild(this.templateCommand);

		this.ui.disposal = new PIXI.Sprite(P3.assets.texture('ui_function_delete'));
		this.ui.disposal.anchor.set(0.5);
		this.ui.disposal.position.set(60 + (this.ui.disposal.width * 0.5), P3.View.height - 70);
		this.ui.disposal.visible = false;
		this.ui.disposal.scale.set(1.2);
		this.ui.main.addChild(this.ui.disposal);

		this.btnScrollUp.visible = false;
		this.btnScrollDown.visible = false;
		this.ui.scrollbar.visible = false;
		this.resize();

		const lastCommand = this.commands[this.commands.length - 1];
		this._fingerScrollBounds.upper = new PIXI.Rectangle((common.STAGE_WIDTH - P3.View.width) * 0.5 + 35, 70, 250, 75);
		this._fingerScrollBounds.lower = new PIXI.Rectangle((common.STAGE_WIDTH - P3.View.width) * 0.5 + 35, 70, 250, 75);
		this._fingerScrollBounds.lower.y = lastCommand.y - lastCommand.height * 0.5 - this._fingerScrollBounds.lower.height;
	}

	destroy() {
		this._destroyed = true;
		this.gameScene = null;
		this.player = null;
		P3.utils.destroyAllSignals(this.signals);
		this.tutorialTweens.removeAll();
		this.tweens.removeAll();

		this.commandContainer.destroy({children: true});
		this.templateCommand.destroy({children: true});
		this.btnScrollDown.destroy({children: true});
		this.btnScrollUp.destroy({children: true});
		this.commandMask.destroy({children: true});
		this.ui.destroy({children: true});

		this.ui.removeAllListeners();

		common.commandInterface = null;
	}

	createUI() {
		const container = new PIXI.Container();
		const states = new P3.ButtonStates();
		states.normal = P3.assets.texture('ui_btn_small_out');
		states.over = P3.assets.texture('ui_btn_small_over');
		states.down = P3.assets.texture('ui_btn_small_down');
		states.icon = P3.assets.texture('ui_btn_add_out');

		container.add = new P3.Button(states);
		container.add.clickSoundName = 'sfx_btn_press_00';
		container.add.overSoundName = 'sfx_btn_rollover_00';
		container.add.signals.click.add(() => {
			if (this.tutorialMode && common.currentLevel === 0) {
				this.tutorialStage = this.ui.toolbox.visible ? 1 : 2;
				this.signals.updateTutorial.dispatch(this.tutorialStage);
			}
			this.toggleToolbox();
		}, this);
		container.add.position.set((common.STAGE_WIDTH * 0.5), 75);

		container.scrollbar = this.createScrollbar(this._maxContainerHeight - 112);
		container.scrollbar.position.set(((common.STAGE_WIDTH - P3.View.width) * 0.5) + 15, 67);
		container.addChild(container.scrollbar);

		this.btnScrollUp = new ScrollButton(P3.assets.texture('ui_btn_scroll_up'));
		this.btnScrollUp.anchor.set(0);
		this.btnScrollUp.position.set(((common.STAGE_WIDTH - P3.View.width) * 0.5) + 10, 20);
		container.addChild(this.btnScrollUp);

		this.btnScrollDown = new ScrollButton(P3.assets.texture('ui_btn_scroll_up'));
		this.btnScrollDown.anchor.set(0);
		this.btnScrollDown.scale.y = -1;
		this.btnScrollDown.position.set(((common.STAGE_WIDTH - P3.View.width) * 0.5) + 10, this._maxContainerHeight);
		container.addChild(this.btnScrollDown);

		return container;
	}

	createScrollbar(height) {
		this.scrollbar = new PIXI.Container();

		this.scrollbar.thumb = new PIXI.mesh.NineSlicePlane(P3.assets.texture('ui_scroll_fg'), 3, 4, 3, 4);
		this.scrollbar.thumb.height = 250;
		this.scrollbar.thumb.width = 36;
		this.scrollbar.thumb.interactive = true;
		this.scrollbar.thumb.buttonMode = true;
		this.scrollbar.thumb.on('pointerdown', this.onScrollbarThumbDown, this);
		this.scrollbar.thumb.on('pointerup', this.onScrollbarThumbUp, this);
		this.scrollbar.thumb.on('pointerupoutside', this.onScrollbarThumbUp, this);

		this.scrollbar.track = new PIXI.Sprite(P3.assets.texture('ui_scroll_bg'));
		this.scrollbar.track.height = height;
		this.scrollbar.track.alpha = 0.4;
		this.scrollbar.track.width = this.scrollbar.thumb.width;

		this.scrollbar.addChild(this.scrollbar.track);
		this.scrollbar.addChild(this.scrollbar.thumb);

		return this.scrollbar;
	}

	onScrollbarThumbUp() {
		this.isScrolling = false;
		if (common.currentLevel === 4) {
			this.gameScene.runButton.y = common.STAGE_HEIGHT - 100;
		}
	}

	onScrollbarThumbDown(e) {
		this.isScrolling = true;
		this.scrollOffset = e.data.getLocalPosition(this.scrollbar.thumb);
	}

	updateScrollThumb() {
		const thumb = this.scrollbar.thumb;
		const thumbRange = this.scrollbar.track.height - thumb.height;

		if (this.isScrolling) {
			const position = this.scrollbar.toLocal(P3.input.touch, common.stage);
			thumb.y = Math.min(thumbRange, Math.max(0, position.y - this.scrollOffset.y));
			this.scrollView.content.y = this.scrollView.contentBounds.height + ((thumb.y / thumbRange) * (this.scrollView.contentBounds.y - this.scrollView.contentBounds.height));
		} else {
			const contentRange = this.scrollView.contentBounds.y - this.scrollView.contentBounds.height;
			const f = (this.scrollView.content.y - this.scrollView.contentBounds.height) / contentRange;
			thumb.y = Math.min(thumbRange, Math.max(0, f * thumbRange));
		}
	}

	updateScrollButtons() {
		if ((this.btnScrollDown.isHeld || this.scrollStates.down) && this.commandContainer.height > 300 &&
			this.commandContainer.y > -this.commandContainer.height + 100) {
			this.commandContainer.y -= 4;
		}

		if ((this.btnScrollUp.isHeld || this.scrollStates.up) && this.commandContainer.height > 300 && this.commandContainer.y < 0) {
			this.commandContainer.y += 4;
		}
	}

	updateScrollFinger() {
		if (!this.isDragging || !this.scrollbar.visible) {
			this.scrollStates.up = false;
			this.scrollStates.down = false;
			return;
		}

		const lastCommand = this.commands[this.commands.length - 1];
		this._fingerScrollBounds.upper = new PIXI.Rectangle(35, 0, 400, 160);		// TODO: dont create a new object here, because this is in update loop. create one object outside and reuse
		this._fingerScrollBounds.lower = new PIXI.Rectangle(35, 0, 400, 160);
		this._fingerScrollBounds.lower.y = lastCommand.y - lastCommand.height * 0.5 - this._fingerScrollBounds.lower.height;

		// TODO: consider making this just be up = contains(x, y) and down = !contains(x, y);
		let input = PIXI.utils.isMobile.any ? P3.input.touch : P3.input.mouse;
		if (this._fingerScrollBounds.upper.contains(input.x, input.y)) {
			this.scrollStates.up = true;
			this.scrollStates.down = false;
		} else if (this._fingerScrollBounds.lower.contains(input.x, input.y)) {
			this.scrollStates.up = false;
			this.scrollStates.down = true;
		} else {
			this.scrollStates.up = false;
			this.scrollStates.down = false;
		}
	}

	update() {
		if (this.selector) {
			this.selector.update();
		}
		this.scrollView.update(P3.Ticker.dt);
		this.updateScrollThumb();
		this.updateScrollFinger();
		this.updateScrollButtons();

		this.tweens.update();
	}

	updateBounds() {
		const bounds = this.scrollView.contentBounds;
		const repeats = this.commands.filter(element => element instanceof RepeatCommand);
		let t = this.commands.length;

		repeats.forEach((element) => {
			t += element.commands.length;
		});

		// TODO: this should not use 61 hardcoded, it should calculate the height of all commands because they can vary in height (e.g. RepeatCommand)
		// preferable we just loopt through all of our commands & get their height.
		bounds.y = (-t * 61) + bounds.height;

		if (bounds.height - bounds.y > 348) {
			bounds.y += 348;
		} else {
			bounds.y = bounds.height;
		}
	}

	toggleToolbox() {
		// in: 106, out: 290 (x)
		const tb = this.ui.toolbox;

		if (this.selector && !(common.currentLevel === 11 && this.tutorialStage === 1)) {
			this.selector.destroy();
		}

		for (const command of this.commands) {
			command.switchTexture(false);

			if (command instanceof RepeatCommand) {
				for (const rcommand of command.commands) {
					rcommand.switchTexture(false);
				}
			}
		}

		if (tb.visible) {
			P3.audio.playSound('sfx_code_library_close');

			new P3.utils.TWEEN.Tween(tb, this.tweens)
				.to({
					y: -tb.height
				}, 250)
				.easing(P3.utils.TWEEN.Easing.Quadratic.Out)
				.onComplete(() => {
					tb.visible = false;

					if (this.tutorialMode && common.currentLevel === 8) {
						this.signals.updateTutorial.dispatch(this.tutorialStage);
					}
				})
				.start();

			new P3.utils.TWEEN.Tween(this.ui.add, this.tweens)
				.to({
					y: 75
				}, 250)
				.easing(P3.utils.TWEEN.Easing.Quadratic.Out)
				.start();

			new P3.utils.TWEEN.Tween(this.ui.add, this.tweens)
				.to({
					rotation: 0
				}, 250)
				.easing(P3.utils.TWEEN.Easing.Quadratic.Out)
				.delay(100)
				.start();
		} else {
			P3.audio.playSound('sfx_code_library_open');
			new P3.utils.TWEEN.Tween(tb, this.tweens)
				.to({
					y: 90
				}, 250)
				.easing(P3.utils.TWEEN.Easing.Quadratic.Out)
				.onStart(() => {
					tb.visible = true;
				})
				.start();

			new P3.utils.TWEEN.Tween(this.ui.add, this.tweens)
				.to({
					y: this.ui.add.y + tb.height - 5
				}, 250)
				.easing(P3.utils.TWEEN.Easing.Quadratic.Out)
				.start();

			new P3.utils.TWEEN.Tween(this.ui.add, this.tweens)
				.to({
					rotation: 45 * PIXI.DEG_TO_RAD
				}, 100)
				.easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
				.delay(150)
				.start();

			if (this.tutorialMode && common.currentLevel === 8) {
				if (this.tutorialStage === 0) {
					this.tutorialStage = 1;
				} else if (this.tutorialStage === 2) {
					this.tutorialStage = 3;
				}

				this.signals.updateTutorial.dispatch(this.tutorialStage);
			} else if (this.tutorialMode && common.currentLevel === 5) {
				if (this.tutorialStage === 0) {
					this.tutorialStage = 1;
				}

				this.signals.updateTutorial.dispatch(this.tutorialStage);
			}
		}
	}

	createToolbox() {
		const container = new PIXI.Container();
		const bg = new PIXI.mesh.NineSlicePlane(P3.assets.texture('add_function_9slice_panel'), 20, 20, 20, 20);
		bg.y = -30;
		container.addChild(bg);

		const Classes = {
			WalkCommand,
			JumpCommand,
			TurnCommand,
			MorphCommand,
			RepeatCommand
		};

		for (const ability of common.levelSettings.abilities) {
			if (ability === 'variables') {
				continue;
			}

			this.toolboxItems.push(new Classes[ability]());
		}

		if (this.toolboxItems.length === 1) {
			this.toolboxWidth = common.commandDimensions.width * 1 + (2 * this.toolboxSpacing);
		}

		let totalHeight = (Math.ceil(this.toolboxItems.length / 2) * common.commandDimensions.height) + 70;
		let x = 0;
		let y = -35;
		for (let i = 0; i < this.toolboxItems.length; i++) {
			if (i % 2 === 0) {
				x = this.toolboxWidth * 0.25;
				y += common.commandDimensions.height + 5;
			} else {
				x = this.toolboxWidth * 0.75;
			}
			this.toolboxItems[i].y = y;
			this.toolboxItems[i].x = x;

			this.toolboxItems[i].grabbable = false;
			this.toolboxItems[i].template = true;

			this.toolboxItems[i].signals.pickup.add(this.onToolboxPickup, this);
			this.toolboxItems[i].signals.release.add(this.checkCommand, this);
			this.toolboxItems[i].signals.touch.add(this.onCommandTouch, this);
			this.toolboxItems[i].signals.move.add(this.onCommandMove, this);
			this.toolboxItems[i].signals.executionComplete.add(this.runCommands, this);

			container.addChild(this.toolboxItems[i]);
		}

		if (this.toolboxItems.length % 2 !== 0) {
			this.toolboxItems[this.toolboxItems.length - 1].x = this.toolboxWidth * 0.5;
		}

		bg.width = this.toolboxWidth;
		bg.height = totalHeight + 20;

		return container;
	}

	onCommandTouch() {
		this.isDragging = true;
	}

	onCommandMove(command) {
		const last = this.commands[this.commands.length - 1];
		let closest = this.commands[0];
		let closestDistance = Infinity;

		for (const com of this.commands) {
			if (com === this.commands[0]) {
				continue;
			}

			if (com instanceof RepeatCommand) {
				com.resize(true, false);

				const collision = utils.collideAABB(command.ui, com.ui, new PIXI.Point(1, 0.75));

				if (collision) {
					com.onCommandMove(command, this.templateCommand);
					com.extended = true;
					this.resize();
					break;
				} else {
					com.resize(false, false);
					com.extended = false;
				}
			}

			if (com.locked) {
				continue;
			}

			const localpos = com.toGlobal(common.stage);

			this.templateCommand.position.x = localpos.x + 105;

			if (command.y <= localpos.y) {
				let target = com.slotPosition.y + command.height - 12;

				const i = this.commands.indexOf(com);
				const prev = this.commands[i - 1];

				if (prev instanceof RepeatCommand && prev.commands.length === 0 && prev.height > 70) {
					target += this.commands[i - 1].height - 100;
				}

				const d = target - command.y;
				com.y = target;

				if (d >= 0 && d < closestDistance) {
					this.templateCommand.y = com.y - command.height + 10 + this.commandContainer.y;

					closestDistance = d;
					closest = com;
				}
			} else {
				com.y = com.slotPosition.y;

				if (command.y > last.y) {
					last.y = last.slotPosition.y + command.height - 12;
					this.templateCommand.y = com.y - command.height + 25 + this.commandContainer.y;
					closest = last;
				}
			}
		}
		this.currentMoveIndex = this.commands.indexOf(closest);
	}

	_destroyCommand(command) {
		command.destroy();

		for (const comm of this.commands) {
			comm.y = comm.slotPosition.y;
		}

		this.templateCommand.visible = false;

		if (this.tutorialMode && common.currentLevel === 0) {
			this.tutorialStage = this.commands.length >= 4 ? 3 : 1;
			this.signals.updateTutorial.dispatch(this.tutorialStage);
		} else if (this.tutorialMode && common.currentLevel === 1) {
			this.tutorialStage = 2;
			this._tutorial2HasDeleted = true;
			this.signals.updateTutorial.dispatch(this.tutorialStage);
		}

		this.ui.disposal.visible = false;

		if (this.ui.toolbox.visible) {
			this.toggleToolbox();
		}

		this.signals.setvisibility.dispatch(this.commands.length > 2);

		this.resize();

		this.updateBounds();

		P3.audio.playSound('sfx_code_block_trashed');
	}

	checkCommand(command) {
		this.isDragging = false;

		for (const c of this.commands) {
			if (this.ui.disposal.visible && utils.collideAABB(command, this.ui.disposal)) {
				// prevent the jump command being deleted in 2nd tutorial
				if (this.tutorialMode && common.currentLevel === 1 && command instanceof JumpCommand) {
					continue;
				}

				this._destroyCommand(command);

				return;
			}

			if (c instanceof RepeatCommand && utils.collideAABB(command, c, new PIXI.Point(1, 0.75))) {
				if (command instanceof RepeatCommand) {
					this.currentMoveIndex = this.commands.indexOf(c) + 1;
					for (const com of this.commands) {
						if (com instanceof RepeatCommand) {
							com.resize(false, false);
						}
					}
					this.resize();
					break;
				}

				c.checkCommand(command);

				for (const comm of this.commands) {
					comm.y = comm.slotPosition.y;
				}

				this.templateCommand.visible = false;
				this.ui.disposal.visible = false;

				if (this.tutorialMode && common.currentLevel === 8) {
					this.tutorialStage = c.commands.length === 1 ? 2 : 4;
					this.signals.updateTutorial.dispatch(this.tutorialStage);
				}

				this.signals.setvisibility.dispatch(this.commands.length > 2);

				this.updateBounds();

				if (this.ui.toolbox.visible) {
					this.toggleToolbox();
				}

				this.resize();

				return;
			}
		}

		// only allow the user to place into the repeat command for level 9 tutorial
		if (this.tutorialMode && common.currentLevel === 8) {
			if (this.ui.toolbox.visible) {
				this.toggleToolbox();
			}

			if (this.commands[1].commands.length <= 0) {
				this.tutorialStage = 0;
			} else {
				this.tutorialStage = 2;
			}

			this.signals.updateTutorial.dispatch(this.tutorialStage);

			this.ui.add.interactive = true;
			this._destroyCommand(command);

			return;
		}

		// prevents commands being placed above the start block even though that should've been impossible  ¯\_(ツ)_/¯
		if (this.currentMoveIndex === 0) {
			command.destroy();

			for (const comm of this.commands) {
				comm.y = comm.slotPosition.y;
			}

			this.templateCommand.visible = false;
			this.ui.disposal.visible = false;

			if (this.tutorialMode && common.currentLevel === 0) {
				this.tutorialStage = this.commands.length >= 4 ? 3 : 1;
				this.signals.updateTutorial.dispatch(this.tutorialStage);
			}

			this.signals.setvisibility.dispatch(this.commands.length > 2);

			this.updateBounds();

			return;
		}

		this.commandContainer.addChild(command);

		command.x = 169;

		for (const comm of this.commands) {
			comm.slotPosition.set(comm.position.x, comm.position.y);
		}

		this.commands.splice(this.currentMoveIndex, 0, command);

		command.placed = true;
		command.interactive = true;
		command.variable = command.defaultVariable;
		command.setText(command.variable);

		if (common.currentLevel === 0 || (common.currentLevel === 1 && command instanceof JumpCommand)) {
			command.grabbable = false;
			command.template = false;
			command.locked = true;
			command.interactive = false;
			command.switchTexture(false);
		}

		if (command.type === Command.TYPE_LOOP) {
			command.onPlace();
		}

		if (this.ui.toolbox.visible) {
			this.toggleToolbox();
		}

		this.resize();
		this.onVariableTouch(command);

		if (this.tutorialMode && common.currentLevel === 0) {
			this.tutorialStage = this.commands.length >= 4 ? 3 : 1;
			this.signals.updateTutorial.dispatch(this.tutorialStage);
		} else if (this.tutorialMode && common.currentLevel === 1) {
			this.tutorialStage = this.commands.length === 5 ? 3 : 0;
			this.signals.updateTutorial.dispatch(this.tutorialStage);
		} else if (this.tutorialMode && this.tutorialStage === 1 && common.currentLevel === 5 && command instanceof MorphCommand) {
			this.tutorialStage = 2;
			this.signals.updateTutorial.dispatch(this.tutorialStage);
		}

		this.templateCommand.visible = false;
		this.ui.disposal.visible = false;

		this.signals.setvisibility.dispatch(this.commands.length > 2);

		this.updateBounds();
	}

	onToolboxPickup(command, e) {
		// copy is the new toolbox version. passed command is the command the user will hold.
		let copy = null;

		switch (command.type) {
			case Command.TYPE_WALK: copy = new WalkCommand(); break;
			case Command.TYPE_JUMP: copy = new JumpCommand(); break;
			case Command.TYPE_TURN: copy = new TurnCommand(); break;
			case Command.TYPE_LOOP: copy = new RepeatCommand(); break;
			case Command.TYPE_TRANSFORM: copy = new MorphCommand(); break;
		}

		copy.position.set(command.position.x, command.position.y, command.position.z);

		copy.signals.pickup.add(this.onToolboxPickup, this);
		copy.signals.release.add(this.checkCommand, this);

		copy.type = command.type;
		copy.dispatched = false;
		copy.grabbable = false;
		copy.template = true;
		this.ui.toolbox.addChild(copy);

		this.ui.toolbox.removeChild(command);
		command.signals.pickup.detachAll();
		command.signals.touch.detachAll();
		command.signals.pickup.add(this.onCommandPickup, this);
		command.signals.move.add(this.onCommandMove, this);
		command.signals.touch.add(this.onCommandTouch, this);
		command.grabbable = true;
		command.template = false;
		command.signals.onVariableTouch.add((c) => {
			this.onVariableTouch(c);
		}, this);

		if (command.type === Command.TYPE_LOOP) {
			command.signals.onResize.add((e) => {
				return this.resize(e);
			}, this);

			command.signals.onPlace.add((command) => {
				return this.onVariableTouch(command);
			}, this);
		}

		this.isDragging = true;

		const newpos = e.data.getLocalPosition(this.ui);

		command.position.set(newpos.x, newpos.y);

		if (![0, 8].includes(common.currentLevel) &&
			(common.currentLevel !== 5 || (common.currentLevel === 5 && this.tutorialStage > 1))) {
			this.ui.disposal.visible = true;
		}

		this.templateCommand.visible = true;
		this.ui.addChild(command);

		this.toolboxItems.splice(this.toolboxItems.indexOf(command), 0, copy);

		P3.audio.playSound('sfx_code_block_added');

		// if the command was only clicked and onMove wasn't fired, this ensure it's added to the back of the array.
		this.currentMoveIndex = this.commands.length - 1;
	}

	/*
	 *	When code is undocumented it's usually because
	 *	I eventually got it to work through trial and error
	 *	and I have no idea how it actually works bye
	 *
	 *  and that says a lot considering none of this code is documented
	 */
	onVariableTouch(command) {
		if (!command.canChangeVariable) {
			return;
		}

		if (this.isTweening) {
			this.needSelector = command;
			return;
		}

		this.needSelector = null;

		if (this.ui.toolbox.visible) {
			this.toggleToolbox();
		}

		if (this.selector) {
			this.selector.destroy();
		}

		this.selector = new VariableSelector(command);

		for (const c of this.commands) {
			c.switchTexture(c === command);

			if (c instanceof RepeatCommand) {
				for (const rcommand of c.commands) {
					rcommand.switchTexture(rcommand === command);
				}
			}
		}

		this.selector.signals.select.add((o) => {
			this.onVariableSelect(o, command);
		}, this);

		this.selector.signals.destroy.add(() => {
			this.isDragging = false;
		}, this);

		let local = command.position.clone();

		if (command.parent instanceof RepeatCommand) {
			local = command.toGlobal(this.ui);

			local.y -= this.commandContainer.y;
			local.x += 15;

			if (!this.ui.scrollbar.visible) {
				local.x += ~~(16 * common.commandScale);
			}
		}

		if (command instanceof MorphCommand) {
			local.x += common.morphSliderOffsets[common.config.language] * common.commandScale;
		}

		this.selector.position.set(local.x + (command.ui.width * 0.5), local.y - (this.selector.height * 0.5) - 6);
		this.commandContainer.addChild(this.selector);

		if (this.tutorialMode && common.currentLevel === 3) {
			this.tutorialStage = 1;
			this.signals.updateTutorial.dispatch(this.tutorialStage);
		} else if (this.tutorialMode && common.currentLevel === 6) {
			this.tutorialStage = 1;
			this.signals.updateTutorial.dispatch(this.tutorialStage);
		} else if (this.tutorialMode && common.currentLevel === 8 && command instanceof RepeatCommand) {
			this.tutorialStage = command.variable === 4 ? 6 : 5;
			this.signals.updateTutorial.dispatch(this.tutorialStage);
		} else if (this.tutorialMode && common.currentLevel === 9 && command instanceof MorphCommand && this.tutorialStage === 0) {
			this.tutorialStage = 1;
			this.signals.updateTutorial.dispatch(this.tutorialStage);
		} else if (this.tutorialMode && common.currentLevel === 9 && command instanceof WalkCommand && this.tutorialStage === 2) {
			this.tutorialStage = 3;
			this.signals.updateTutorial.dispatch(this.tutorialStage);
		} else if (this.tutorialMode && common.currentLevel === 11 && command instanceof MorphCommand) {
			this.tutorialStage = this.tutorialStage === 0 ? 1 : this.tutorialStage === 2 ? 3 : 4;
			this.signals.updateTutorial.dispatch(this.tutorialStage);
		} else if (this.tutorialMode && common.currentLevel === 13 && command instanceof MorphCommand) {
			this.tutorialStage = 1;
			this.signals.updateTutorial.dispatch(this.tutorialStage);
		}
	}

	onVariableSelect(option, command) {
		if (option.type === 'number') {
			if ((command.variable <= 1 && option.value < 1) ||
				(command.variable >= 10 && option.value > -1)) {
				return;
			}

			command.variable += option.value;
			command.defaultVariable = command.variable;
			command.setText(command.variable);

			if (this.tutorialMode && common.currentLevel === 8 && command.variable === 4 && command instanceof RepeatCommand) {
				command.canChangeVariable = false;
				this.selector.destroy();
				this.tutorialStage = 6;
				this.gameScene.runButton.visible = true;
				this.signals.updateTutorial.dispatch(this.tutorialStage);
			} else if (this.tutorialMode && common.currentLevel === 6 && command.variable === 3) {
				command.canChangeVariable = false;
				this.selector.destroy();
				this.tutorialStage = 2;
				this.gameScene.runButton.visible = true;
				this.signals.updateTutorial.dispatch(this.tutorialStage);
			} else if (this.tutorialMode && common.currentLevel === 9 && command.variable === 4 && this.tutorialStage === 3) {
				command.canChangeVariable = false;
				this.selector.destroy();
				this.tutorialStage = 4;
				this.signals.updateTutorial.dispatch(this.tutorialStage);
			}
		} else {
			if (common.currentLevel === 3 && option.val.value === 'right') {
				return;
			}

			command.setText(option.val.value);
			command.variable = option.val.value;
			command.defaultVariable = command.variable;

			this.selector.destroy();

			for (const command of this.commands) {
				command.switchTexture();

				if (command instanceof RepeatCommand) {
					for (const rcommand of command.commands) {
						rcommand.switchTexture();
					}
				}
			}

			if (this.tutorialMode && common.currentLevel === 3) {
				this.tutorialStage = 2;
				this.gameScene.runButton.visible = true;
				this.signals.updateTutorial.dispatch(this.tutorialStage);
			} else if (this.tutorialMode && common.currentLevel === 5 && this.tutorialStage === 2) {
				this.tutorialStage = 3;
				this.gameScene.runButton.visible = true;
				this.signals.updateTutorial.dispatch(this.tutorialStage);
			} else if (this.tutorialMode && common.currentLevel === 11 && this.tutorialStage === 1 && option.val.value === 'cannonbolt') {
				this.tutorialStage = 2;
				this.signals.updateTutorial.dispatch(this.tutorialStage);
			} else if (this.tutorialMode && common.currentLevel === 9 && this.tutorialStage === 1 && option.val.value === 'fourarms') {
				this.tutorialStage = 2;
				this.signals.updateTutorial.dispatch(this.tutorialStage);
			} else if (this.tutorialMode && common.currentLevel === 11 && this.tutorialStage === 3 && option.val.value === 'fourarms') {
				this.tutorialStage = 4;
				this.signals.updateTutorial.dispatch(this.tutorialStage);
			} else if (this.tutorialMode && common.currentLevel === 13 && this.tutorialStage === 1 && option.val.value === 'overflow') {
				this.tutorialStage = 2;
				this.signals.updateTutorial.dispatch(this.tutorialStage);
			}
		}
	}

	onCommandPickup(command) {
		if (this.selector) {
			this.selector.destroy();
		}

		if (!this._tutorial2HasDeleted && ![0, 8].includes(common.currentLevel) ||
			(common.currentLevel === 5 && this.tutorialStage >= 1)) {
			this.ui.disposal.visible = true;
		}

		this.templateCommand.visible = true;

		if (command.parent instanceof RepeatCommand) {
			command.parent._onCommandPickup(command);

			this.resize();

			return;
		}

		const index = this.commands.indexOf(command);

		if (this.tutorialMode && common.currentLevel === 1) {
			this.tutorialStage = this._tutorial2HasDeleted ? 3 : 1;
			this.signals.updateTutorial.dispatch(this.tutorialStage);
		}

		const localpos = command.toGlobal(this.ui.main.position);

		this.isDragging = true;
		this.commands.splice(index, 1);
		command.attached = false;
		this.ui.addChild(command);
		command.x = localpos.x + 25;

		P3.audio.playSound('sfx_code_block_move_position');

		this.resize();
	}

	onPointerDown(e) {
		if (this.selector && !this.tutorialMode) {
			if (e.target && e.target.pluginName) {
				return;
			}

			this.selectorPoint = new PIXI.Point(0, 0);
			this.selector.destroy();
			this.selector = null;

			for (const c of this.commands) {
				c.switchTexture(false);

				if (c instanceof RepeatCommand) {
					for (const rcommand of c.commands) {
						rcommand.switchTexture(false);
					}
				}
			}
		}
	}

	/**
	 * Creates a start command, an end command and blank command inbetween.
	 */
	createCommands() {
		const borderOffset = {
			x : 60,
			y : 20
		};

		// start command
		const startCommand = new Command('ui_function_start');

		startCommand.grabbable = false;
		startCommand.x = (startCommand.width * 0.5) + borderOffset.x;
		startCommand.y = (startCommand.height * 0.5) + borderOffset.y;

		this.commandContainer.addChild(startCommand);

		startCommand.slotPosition.set(startCommand.position.x, startCommand.position.y);

		// end command
		const endCommand = new Command('ui_function_stop');

		endCommand.grabbable = false;
		endCommand.x = (endCommand.width * 0.5) + borderOffset.x;
		endCommand.y = 200 - (endCommand.height * 0.5);

		this.commandContainer.addChild(endCommand);

		endCommand.slotPosition.set(endCommand.position.x, endCommand.position.y);

		// add those to commands list
		this.commands.push(startCommand);
		this.commands.push(endCommand);
	}

	/**
	 * please dont ask me how this works
	 * repeat commands are such a fucking pain in the ass. idk why i cant just use the height of the repeat command
	 * this needs a serious overhaul., this whole class just needs to be thrown away
	 */
	resize(fromIndex = 0) {
		for (let i = fromIndex + 1; i < this.commands.length; i++) {
			const current = this.commands[i];
			const previous = this.commands[i - 1];

			if (current.type === Command.TYPE_EMPTY && current.occupied) {
				continue;
			}

			current.y = previous.y + (previous.height * 0.5) + (current.height * 0.5) - common.commandTopBottomOffset;

			if (i === 1) {
				current.y -= 4;
			}

			if (i === this.commands.length - 1) {
				current.y -= common.commandTopBottomOffset;
			}

			const heldCommandHeight = (common.commandDimensions.height * 0.5) - common.commandTopBottomOffset;

			if ((previous instanceof RepeatCommand && previous.commands.length > 0) &&
				(current instanceof RepeatCommand && current.commands.length > 0)) {
				current.y += (heldCommandHeight * previous.commands.length * 1.15) - (heldCommandHeight * current.commands.length * 1.15);
			} else if (current instanceof RepeatCommand && current.commands.length <= 0 && previous instanceof RepeatCommand && previous.commands.length <= 0) {
				current.y += 10;
			} else if (current instanceof RepeatCommand && current.commands.length > 0) {
				current.y -= (heldCommandHeight * current.commands.length * 1.15) - 1;
			} else if (previous instanceof RepeatCommand && previous.commands.length > 0) {
				current.y += heldCommandHeight * previous.commands.length * 1.15 - 4;
			} else if (current instanceof RepeatCommand && !(previous instanceof RepeatCommand)) {
				current.y -= common.commandTopBottomOffset - 1;
			} else if (previous instanceof RepeatCommand && !(current instanceof RepeatCommand)) {
				current.y += common.commandTopBottomOffset;
			}

			if (current instanceof RepeatCommand && current.commands.length <= 0 && previous instanceof RepeatCommand) {
				current.y -= 10;
			} else if (current instanceof RepeatCommand && previous instanceof RepeatCommand && previous.commands.length <= 0) {
				current.y += 12;
			}

			if (current instanceof RepeatCommand && current.extended) {
				current.y -= 35;
			} else if (previous instanceof RepeatCommand && previous.extended) {
				current.y += 35;
			}

			current.slotPosition.set(current.position.x, current.position.y);
		}

		let total = this.commands.length;
		this.commands.forEach(element => {
			if (element instanceof RepeatCommand) {
				total += element.commands.length;
			}
		});
		this.signals.blockchange.dispatch(total - 2);

		if (this.commandContainer.height > this.commandContainer.mask.height) {
			if (!this.ui.scrollbar.visible && !this.isTweening) {
				new P3.utils.TWEEN.Tween(this.ui.main.position, this.tweens)
					.to({
						x: ((common.STAGE_WIDTH - P3.View.width) * 0.5)
					}, 250)
					.onStart(() => {
						this.isTweening = true;
						this.btnScrollUp.visible = true;
						this.btnScrollDown.visible = true;
						this.ui.scrollbar.visible = true;
					})
					.easing(P3.utils.TWEEN.Easing.Linear.None)
					.onComplete(() => {
						this.isTweening = false;

						if (this.needSelector) {
							this.onVariableTouch(this.needSelector);
						}
					})
					.start();
			}
		} else {
			this.commandContainer.y = 0;

			if (this.ui.scrollbar.visible && !this.isTweening) {
				new P3.utils.TWEEN.Tween(this.ui.main.position, this.tweens)
					.to({
						x: ((common.STAGE_WIDTH - P3.View.width) * 0.5) - 50
					}, 250)
					.onStart(() => {
						this.isTweening = true;
						this.btnScrollUp.visible = false;
						this.btnScrollDown.visible = false;
						this.ui.scrollbar.visible = false;
					})
					.onComplete(() => {
						this.isTweening = false;
					})
					.easing(P3.utils.TWEEN.Easing.Linear.None)
					.start();
			}
		}
	}

	lock() {
		if (!this.canStart || this.running) {
			return false;
		}

		this.canStart = false;
		this.running = true;

		const newArray = [];

		this.commandsPre = this.commands;

		let loopCommands = 0;

		for (const command of this.commands) {
			if (command.type === Command.TYPE_STATIC || command.type === Command.TYPE_EMPTY) {
				continue;
			}

			this.commandCount++;

			if (command.type === Command.TYPE_LOOP) {
				if (command.variable <= 0) {
					command.variable = command.defaultVariable;
					command.setText(command.defaultVariable);
				}

				for (let i = 0; i < command.variable; i++) {
					for (const rcommand of command.commands) {
						if (rcommand.type === Command.TYPE_STATIC || rcommand.type === Command.TYPE_EMPTY) {
							continue;
						}

						// check if the command has an assigned parameter.
						if (!rcommand.variable || rcommand.variable === -1) {
							rcommand.variable = rcommand.defaultVariable;
							rcommand.setText(rcommand.defaultVariable);
						}

						if (typeof rcommand.variable === 'number') {
							for (let i = 0; i < rcommand.variable; i++) {
								newArray.push(rcommand);
							}
						} else {
							newArray.push(rcommand);
						}
					}
				}

				loopCommands = command.commands.length;

				continue;
			}

			// check if the command has an assigned parameter.
			if (!command.variable || command.variable === -1) {
				command.variable = command.defaultVariable;
				command.setText(command.defaultVariable);
			}

			if (typeof command.variable === 'number') {
				for (let i = 0; i < command.variable; i++) {
					newArray.push(command);
				}
			} else {
				newArray.push(command);
			}
		}

		this.commandCount += loopCommands;

		this.commands = [];
		this.commands = this.commands.concat(newArray);
		this.stepCount += newArray.length;

		if (this.commands.length <= 0) {
			this.canStart = true;
			this.running = false;
			this.commands = this.commandsPre;

			return false;
		}

		this.ui.interactive = false;
		this.ui.add.visible = false;
		this.commandContainer.interactive = false;
		this.commandContainer.interactiveChildren = false;

		if (this.selector) {
			this.selector.destroy();
		}

		if (this.ui.toolbox.visible) {
			this.toggleToolbox();
		}

		return true;
	}

	start() {
		this.lock();

		this.signals.start.dispatch();

		this.runCommands();
	}

	runCommands() {
		if (!this.canRun || !this.running) {
			return;
		}

		this.currentCommand++;

		if (this.currentCommand > this.commands.length - 1 || !this.commands[this.currentCommand]) {
			this.endCommands();

			return;
		}

		for (const command of this.commands) {
			command.switchTexture(command === this.commands[this.currentCommand]);
			command.darken(command !== this.commands[this.currentCommand]);

			if (command === this.commands[this.currentCommand]) {
				command.parent.addChild(command);
			}
		}

		this.commands[this.currentCommand].execute();

		P3.audio.playSound('sfx_code_block_processed_success');
	}

	endCommands() {
		this.currentCommand = 0;
		this.onFail('passive');
	}

	windowResize() {
		const xPos = (common.STAGE_WIDTH - P3.View.width) * 0.5;

		this.ui.main.x = xPos + (this.ui.scrollbar.visible ? 0 : -this.ui.scrollbar.width);

		this.ui.scrollbar.position.set(xPos + 12, 67);
		this.btnScrollDown.position.set(xPos + 10, this._maxContainerHeight);
		this.btnScrollUp.position.set(xPos + 10, 20);
	}

	/**
	 * Resets some game information ready for next play.
	 */
	reset(causeOfDeath) {
		const anim = causeOfDeath === 'passive' ? 'fail1' : 'die';

		P3.audio.playSound('sfx_code_fail');

		if (causeOfDeath === 'instantDeath') {
			P3.audio.playSound('sfx_omnitrix_transform_back_01');

			this.resetData();

			this.player.setSkin('ben');

			this.player.signals.transformEffect.dispatch(0, false);

			this.player.mesh.spine.state.clearListeners();
			this.player.mesh.spine.state.setAnimation(0, 'transform_back', false);

			this.player.mesh.spine.state.tracks[0].listener = {
				complete: () => {
					this.player.mesh.spine.state.clearListeners();
					this.player.mesh.spine.state.setAnimation(0, this.player.animations.idle, true);
				}
			};

			return;
		}

		this.player.transform('ben', false, () => {
			if (anim === 'die') {
				P3.audio.playSound('vo_ben_oohoof_00');
			}

			this.player.mesh.spine.state.setAnimation(0, anim, false);
			this.player.mesh.spine.state.tracks[0].listener = {
				event: (track, event) => {
					if (event.stringValue === 'sfx_transformback') {
						this.player.signals.transformEffect.dispatch(0, false);
					}
				},
				complete: () => {
					P3.audio.playSound('sfx_omnitrix_transform_back_01');

					this.player.mesh.spine.state.setAnimation(0, 'transform_back', false);

					this.resetData();

					this.player.mesh.spine.state.tracks[0].listener = {
						complete: () => {
							this.player.mesh.spine.state.clearListeners();
							this.player.mesh.spine.state.setAnimation(0, this.player.animations.idle, true);
						}
					};
				}
			};
		}, false);
	}

	resetData() {
		for (const command of this.commands) {
			command.switchTexture(false);
			command.darken(false);
		}

		this.ui.add.visible = true;
		this.ui.interactive = true;
		this.commandContainer.interactive = true;
		this.commandContainer.interactiveChildren = true;
		this.commands = this.commandsPre; // Sets the command array back to the previous array.
		this.player.mesh.rotation.y = 0;
		this.player.direction = Player.DIRECTION.RIGHT;
		this.canRun = false;
		this.currentCommand = -1;
		this.player.setTilePosition(this.player.startTile.x, this.player.startTile.y);
		this.canRun = true;
		this.canStart = true;
		this.player.dead = false;
		this.player.mesh.position.y = 0.4;
		this.signals.end.dispatch();
		this.canStart = true;
	}

	onFail(causeOfDeath) {
		if (!this.running) {
			return;
		}

		common.t.track('game losses', {gamelevel: common.currentLevel, gameloss: 1});

		this.running = false;
		this.reset(causeOfDeath);
		this.stepCount = 0;
		this.commandCount = 0;
		this.player.coins = 0;
		this.player.signals.coinpickup.dispatch();
	}

	onSuccess() {
		for (const command of this.commands) {
			command.switchTexture(false);
		}

		common.t.track('game wins', {gamelevel: common.currentLevel, gamewin: 1});

		this.player.playCompleteAnim();

		const remainingCommands = this.commands.slice(this.currentCommand + 1);
		this.signals.levelcomplete.dispatch({
			commands : this.commandCount,
			steps    : this.stepCount,
			coins    : this.player.coins
		}, remainingCommands);

		this.stepCount = 0;
		this.commandCount = 0;
		this.player.coins = 0;
	}
}
